import {
  ApiButton,
  Box,
  Button,
  Divider,
  HtmlTrans,
  KeyValue,
  nameRegExp,
  SectionHeader,
  TextFieldForm,
  Typography,
  WordCountArea,
} from 'web-library';
import React, { useEffect, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { UploadedFile } from 'web-library/models/UploadedFile';
import { MutationStatus } from 'react-query';
import { useMediaQuery, useTheme } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import { useHistory, useLocation } from 'react-router-dom';
import { RequestFileDropZone } from '../../../components/access-configuration/RequestFileDropZone';
import { ID_TYPE_MENU_ITEMS } from './CandidateProfileForm/CandidateProfilePersonalParticulars';
import ControlledAutocomplete from '../../../components/form-items/ControlledAutocomplete';
import {
  getIExamDropdownItemLabel,
  getIExamMasterCodeSelected,
} from '../logic/useCandidateProfileForm';
import {
  candidateProfileRequestForAmendmentSchema,
  CandidateProfileRequestForAmendmentSchemaData,
} from '../schema/candidateProfileRequestForAmendmentSchema';
import { usePrCitizenshipForm } from '../../../pages/userAccountManagement/CreateEditViewCandidateAccountDetails/usePrCitizenshipForm';
import { useGetIExamMasterCode } from '../../../api-refactored/user/useGetIExamMasterCode';
import { IExamMasterCodeItem } from '../../../api/user/model';
import { useGetProfileAmendmentRequestFileConfigs } from '../../../api-refactored/profile-amendment-requests/useGetProfileAmendmentRequestFileConfigs';
import {
  useHandleSubmitForApprovalWithFileUpload,
  useSetCandidatePrStatusCitizenshipType,
} from '../logic/useCandidateProfileRequestForAmendmentDetails';
import { useGetUserDetails } from '../../../api-refactored/user/useGetUserDetails';
import { CreateCandidateFormLocationState } from '../logic/useCreateCandidateForm';
import CandidateProfileRequestAmendmentDialog from './CandidateProfileForm/CandidateProfileRequestAmendmentDialog';
import RouteKey from '../../../routes/key';
import ControlledDatePicker from "web-library/stories/atom/DatePicker/ControlledDatePicker";

const useStyles = makeStyles()((theme) =>
({
  responsiveFlexContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  dropdownRemarksStatement: {
    marginBottom: theme.spacing(4),
  },
   inputLabel: {
      marginBottom: theme.spacing(1),
      color: theme.color.text.textHeader,
    },
  formElementContainer: {
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(3),
    },
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
    },
    width: '100%',
  },
  halfWidthLeftItem: {
    width: '50%',
    paddingRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      paddingRight: 0,
    },
  },
  halfWidthRightItem: {
    width: '50%',
    paddingLeft: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      paddingLeft: theme.spacing(0),
    },
  },
  divider: {
    marginTop: theme.spacing(7),
    [theme.breakpoints.between('xs', 'md')]: {
      marginTop: theme.spacing(5),
    },
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(3),
    },
  },
  changeRequestTitle: {
    marginBottom: theme.spacing(6),
  },
  remarks: {
    maxWidth: '640px',
  },
  errorText: {
    color: theme.color.state.stateError,
    marginBottom: theme.spacing(2),
  },
  buttonContainer: {
    marginBottom: theme.spacing(7),
  },
  submitForApprovalButton: {
    marginLeft: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginLeft: theme.spacing(0),
      marginTop: theme.spacing(2),
    },
  },
}));

const { profile } = RouteKey.child;
const { candidateDetail } = RouteKey.profile.child;
const openModal = (
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>,
  files: number,
  setErrorMessage: React.Dispatch<React.SetStateAction<string>>,
  t: TFunction
) => {
  if (files > 0) {
    setOpenModal(true);
  } else {
    setErrorMessage(t('profile->>CreateCandidateForm.MissingSupportingDocumentMessage'));
  }
};

const handleSubmitForm = (
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>,
  handleSubmitForApproval: () => void
) => {
  setOpenModal(false);
  handleSubmitForApproval();
};
const getFormDefaultValues = (defaultValues?: any) => ({
  name: defaultValues?.name,
  userIdType: defaultValues?.IdType,
  NricFinPassportNo: defaultValues?.NricFin,
  dateOfBirth: defaultValues?.dateOfBirth,
  citizenship: defaultValues?.citizenship,
  prStatus: defaultValues?.prStatus,
});
const CandidateProfileRequestForAmendmentDetails = () => {
  const { classes } = useStyles();
  const history = useHistory();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const location = useLocation<CreateCandidateFormLocationState>();
  if (location?.state?.formData?.formDataDefaultValues === undefined) {
    history.push(`/${profile}/${candidateDetail.route}`);
  }
  const { data: userData } = useGetUserDetails();

  const isSingpassUser = userData?.data?.credentialType === 'SINGPASS';
  const [openConfirmChangesModal, setOpenConfirmChangesModal] = useState<boolean>(false);

  const singPassRedirectObjectString = sessionStorage.getItem('urlToBeRedirectedAfterLoggedIn');
  let singPassRedirectObject: any;
  if (singPassRedirectObjectString) {
    const redirectObject = JSON.parse(singPassRedirectObjectString);
    singPassRedirectObject = getFormDefaultValues(redirectObject.from?.oldValues);
  }

  const formDefaultValues = getFormDefaultValues(location?.state?.formData?.formDataDefaultValues);

  const [apiButtonStatus, setApiButtonStatus] = useState<MutationStatus>('idle');
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [prStatus, setPrStatus] = useState<IExamMasterCodeItem | undefined | {}>();
  const [citizenshipType, setCitizenshipType] = useState<IExamMasterCodeItem | undefined | {}>();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [uploadFileErrorMessage, setUploadFileErrorMessage] = useState<string>('');

  const formHook = useForm<CandidateProfileRequestForAmendmentSchemaData>({
    mode: 'onSubmit',
    resolver: joiResolver(candidateProfileRequestForAmendmentSchema),
    defaultValues: formDefaultValues,
  });

  const { control, formState: { errors }, handleSubmit, setValue, trigger, watch } = formHook;
  const selectedIdType = watch('IdType');

  const { data: profileAmendmentRequestFileConfigs } = useGetProfileAmendmentRequestFileConfigs();

  const { data: iExamsData } = useGetIExamMasterCode();
  const { renamedPrStatusWithSorting } = usePrCitizenshipForm({
    iExamsData,
  });

  const handleGoBackToProfile = () => history.push('.');

  const { handleIdTypeOnChange, handlePrStatusOnChange } = useSetCandidatePrStatusCitizenshipType({
    setPrStatus,
    setCitizenshipType,
    formHook,
  });

  const { handleSubmitForApproval } = useHandleSubmitForApprovalWithFileUpload({
    setApiButtonStatus,
    setErrorMessage,
    setUploadFileErrorMessage,
    files,
    setFiles,
    formHook,
  });

  const prepopulateDropdownListValues = () => {
    setPrStatus(formDefaultValues?.prStatus);
    setCitizenshipType(formDefaultValues?.citizenship);
  };

  useEffect(() => {
    if (!selectedIdType) {
      trigger('NricFinPassportNo');

      if (prStatus === undefined || citizenshipType === undefined) {
        prepopulateDropdownListValues();
      }
    }
  });

  return (
    <form onSubmit={() => handleSubmit(() => { })}>
      <Box className={classes.dropdownRemarksStatement}>
        <SectionHeader title={t('profile->>CreateCandidateForm.PersonalParticulars')} />
        <HtmlTrans i18nKey="profile->>CreateCandidateForm.RequestForAmendmentStatement" />
      </Box>
      <TextFieldForm
        className={classes.dropdownRemarksStatement}
        form={formHook}
        isInputTextUpperCase
        onChange={(e) => setValue('name', e.target.value.toUpperCase())}
        label={t('profile->>CreateCandidateForm.Name')}
        placeholder={t('profile->>CreateCandidateForm.EnterName')}
        defaultValue={formDefaultValues?.name}
        name="name"
        type="text"
        error={!!errors.name}
        helperText={errors?.name?.message?.toString()}
        fullWidth
        regExpToBlock={nameRegExp}
        inputProps={{
          readOnly: isSingpassUser,
        }}
      />
      <Box className={classes.responsiveFlexContainer}>
        <Box className={`${classes.formElementContainer} ${classes.halfWidthLeftItem}`}>
          <ControlledAutocomplete
            disableClearable
            label={t('profile->>CreateCandidateForm.IDType')}
            control={formHook.control}
            name="IdType"
            id="inputSelect-idType"
            error={!!formHook.formState.errors?.IdType}
            helperText={formHook.formState.errors?.IdType?.message?.toString()}
            options={ID_TYPE_MENU_ITEMS}
            onChange={handleIdTypeOnChange}
            getOptionLabel={({ children }) => children}
            placeholder="Select ID Type"
            defaultValue={formDefaultValues?.userIdType}
            transform={{
              input: (selectedOption, options) =>
                options.find((option) => option.value === selectedOption) ?? null,
              output: (selectedOption) => (selectedOption ? selectedOption.value : null),
            }}
            readOnly
            disabled={isSingpassUser}
          />
        </Box>
        <Box className={`${classes.formElementContainer} ${classes.halfWidthRightItem}`}>
          <TextFieldForm
            form={formHook}
            label={t('profile->>CreateCandidateForm.NRIC/FIN/Passport No.')}
            name="NricFinPassportNo"
            type="text"
            defaultValue={formDefaultValues?.NricFinPassportNo}
            error={!!errors.NricFinPassportNo}
            helperText={errors?.NricFinPassportNo?.message?.toString()}
            fullWidth
            disabled={!selectedIdType || isSingpassUser}
          />
        </Box>
      </Box>
      <ControlledDatePicker
          name="dateOfBirth"
          control={control}
          className={classes.dropdownRemarksStatement}
          label={t('profile->>CreateCandidateForm.DateOfBirth')}
          inputVariant="filled"
          error={!!errors?.dateOfBirth}
          helperText={errors?.dateOfBirth?.message?.toString()}
          autoOk
          disableFuture
          disabled={isSingpassUser}
      />
      <ControlledAutocomplete<IExamMasterCodeItem, false, false, undefined>
        className={classes.dropdownRemarksStatement}
        key={`${selectedIdType}-prStatus`}
        control={control}
        label={t('exam->>ReviewExam.PrStatus')}
        placeholder="Select pr status"
        name="prStatus"
        id="input-select-prStatus"
        error={!!errors?.prStatus}
        helperText={errors?.prStatus?.message?.toString()}
        fullWidth
        disabled={selectedIdType === undefined || selectedIdType === 'P' || isSingpassUser}
        onChange={handlePrStatusOnChange}
        options={
          renamedPrStatusWithSorting ? (renamedPrStatusWithSorting as IExamMasterCodeItem[]) : []
        }
        defaultValue={formDefaultValues?.prStatus}
        getOptionLabel={getIExamDropdownItemLabel}
        isOptionEqualToValue={getIExamMasterCodeSelected}
        readOnly
      />
      <ControlledAutocomplete<IExamMasterCodeItem, false, false, undefined>
        className={classes.dropdownRemarksStatement}
        key={`${selectedIdType}-citizenshipType`}
        control={control}
        label={t('exam->>ReviewExam.CitizenshipType')}
        name="citizenship"
        placeholder="Select citizenship type"
        id="input-select-citizenship"
        disabled
        getOptionLabel={getIExamDropdownItemLabel}
        isOptionEqualToValue={getIExamMasterCodeSelected}
        options={iExamsData ? iExamsData.citizenship : []}
        defaultValue={formDefaultValues?.citizenship}
      />
      <Divider className={classes.divider} />
      <Typography className={classes.changeRequestTitle} variant="h3">
        {t('profile->>CreateCandidateForm.ChangeRequest')}
      </Typography>
      <KeyValue
        keyText={t('profile->>CreateCandidateForm.Action')}
        valueText={<Typography>{t('profile->>CreateCandidateForm.Edit')}</Typography>}
        variant="h6"
      />
      <KeyValue
        keyText={t('profile->>CreateCandidateForm.RequestType')}
        valueText={
          <Typography>{t('profile->>CreateCandidateForm.PersonalParticulars')}</Typography>
        }
        variant="h6"
      />
      <WordCountArea
          form={formHook}
          name="remarks"
          className={`${classes.dropdownRemarksStatement} ${classes.remarks}`}
          error={!!errors.remarks}
          label={t('admin->>UserGroups.Remarks')}
          placeholder={t('admin->>UserGroups.EnterRemarks')}
          maxLength={250}
          minRows={4}
          // CPEPOKPNEH-3391
          onChange={(e) => {
              const trimmedValue = e.target.value.slice(0, 250); // enforce maxLength
              formHook.setValue('remarks', trimmedValue);
          }}
      />
      <Typography className={classes.dropdownRemarksStatement}>
        {t('profile->>CreateCandidateForm.ChangeRequestStatement')}
      </Typography>
      <Typography className={classes.inputLabel} variant="body1">
        {t('specialConsideration->>ApplyForSpecialConsideration.uploadInstruction')}
      </Typography>
      {profileAmendmentRequestFileConfigs && (
        <Box className={classes.remarks}>
          <RequestFileDropZone
            fileConfig={profileAmendmentRequestFileConfigs}
            files={files}
            setFiles={setFiles}
            isLoading={apiButtonStatus === 'loading'}
            isEditable
          />
        </Box>
      )}
      {errorMessage && (
        <Typography className={classes.errorText} variant="body2">
          {errorMessage}
        </Typography>
      )}
      {uploadFileErrorMessage && (
        <Typography className={classes.errorText} variant="body2">
          {uploadFileErrorMessage}
        </Typography>
      )}
      <Box className={`${classes.responsiveFlexContainer} ${classes.buttonContainer}`}>
        <Button
          id="button-candidateProfile-cancel"
          variant="outlined"
          fullWidth={isMobile}
          onClick={handleGoBackToProfile}
          disabled={apiButtonStatus === 'loading'}
        >
          {t('profile->>ProfileAmendmentRequests.Cancel')}
        </Button>
        <ApiButton
          className={classes.submitForApprovalButton}
          id="button-candidateProfile-submit-for-approval"
          status={apiButtonStatus}
          onClick={() =>
            openModal(setOpenConfirmChangesModal, files.length, setUploadFileErrorMessage, t)
          }
          fullWidth={isMobile}
          type="button"
        >
          {t('profile->>CreateCandidateForm.SubmitForApproval')}
        </ApiButton>
      </Box>
      <CandidateProfileRequestAmendmentDialog
        open={openConfirmChangesModal}
        onClose={() => setOpenConfirmChangesModal(false)}
        onConfirm={() => handleSubmitForm(setOpenConfirmChangesModal, handleSubmitForApproval)}
        formHook={formHook}
        singPassRedirectObject={isSingpassUser ? singPassRedirectObject : undefined}
      />
    </form>
  );
};

export default CandidateProfileRequestForAmendmentDetails;
